import type { VoiceCaptureResult, VoiceCaptureSession, VoiceCapturePort } from '../../../ports/VoiceCapturePort';
import { VoiceCaptureError } from '../../../ports/VoiceCapturePort';

const PREFERRED_MIME_TYPES = ['audio/webm;codecs=opus', 'audio/webm', 'audio/ogg;codecs=opus', 'audio/mp4'];

export class BrowserMediaRecorder implements VoiceCapturePort {
  static isSupported(): boolean {
    return Boolean(
      typeof navigator !== 'undefined' &&
        typeof navigator.mediaDevices?.getUserMedia === 'function' &&
        typeof MediaRecorder !== 'undefined'
    );
  }

  async start(): Promise<VoiceCaptureSession> {
    if (!BrowserMediaRecorder.isSupported()) {
      throw new VoiceCaptureError('unsupported', 'Browser microphone recording is not supported here.');
    }

    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch (err) {
      throw classifyStartError(err);
    }

    const cleanupTracks = () => {
      stream.getTracks().forEach((track) => track.stop());
    };

    const mimeType = selectMimeType();
    let recorder: MediaRecorder;
    try {
      recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
    } catch (err) {
      cleanupTracks();
      throw new VoiceCaptureError('recording-failed', 'Could not start microphone recording.', err);
    }

    const chunks: Blob[] = [];
    const startedAt = Date.now();
    let completed = false;
    let cancelled = false;

    recorder.ondataavailable = (event: BlobEvent) => {
      if (!cancelled && event.data && event.data.size > 0) {
        chunks.push(event.data);
      }
    };

    const stop = () =>
      new Promise<VoiceCaptureResult>((resolve, reject) => {
        if (completed) {
          reject(new VoiceCaptureError('recording-failed', 'Voice recording has already ended.'));
          return;
        }
        completed = true;

        recorder.onerror = (event: Event) => {
          cleanupTracks();
          const error = 'error' in event ? (event as ErrorEvent).error : undefined;
          reject(new VoiceCaptureError('recording-failed', 'Voice recording failed.', error));
        };
        const resolveStop = () => {
          cleanupTracks();
          const type = mimeType || chunks[0]?.type || 'audio/webm';
          resolve({
            blob: new Blob(chunks, { type }),
            mimeType: type,
            durationMs: Math.max(0, Date.now() - startedAt),
          });
        };
        recorder.onstop = resolveStop;

        if (recorder.state === 'inactive') {
          resolveStop();
          return;
        }
        recorder.stop();
      });

    const cancel = () =>
      new Promise<void>((resolve) => {
        if (completed) {
          resolve();
          return;
        }
        completed = true;
        cancelled = true;
        chunks.length = 0;
        recorder.ondataavailable = null;
        recorder.onerror = null;
        const resolveCancel = () => {
          cleanupTracks();
          resolve();
        };
        recorder.onstop = resolveCancel;
        if (recorder.state === 'inactive') {
          resolveCancel();
          return;
        }
        recorder.stop();
      });

    try {
      recorder.start();
    } catch (err) {
      cleanupTracks();
      throw new VoiceCaptureError('recording-failed', 'Could not start microphone recording.', err);
    }

    return {
      mimeType: mimeType || 'audio/webm',
      stop,
      cancel,
    };
  }
}

function selectMimeType(): string {
  for (const mimeType of PREFERRED_MIME_TYPES) {
    if (MediaRecorder.isTypeSupported(mimeType)) {
      return mimeType;
    }
  }
  return '';
}

function classifyStartError(err: unknown): VoiceCaptureError {
  const name = typeof err === 'object' && err && 'name' in err ? String((err as { name: unknown }).name) : '';
  if (name === 'NotAllowedError' || name === 'PermissionDeniedError' || name === 'SecurityError') {
    return new VoiceCaptureError('permission-denied', 'Microphone permission was denied.', err);
  }
  return new VoiceCaptureError('recording-failed', 'Could not start microphone recording.', err);
}
